/**
 * Authorize Execution Handoff — the second, separate human decision after
 * EITL approval.
 *
 * Approval judged the recommendation. Authorization directs that the
 * selected provider be linked into OMNΛS procurement/execution truth
 * (BidPackage awarded, quote statuses, one ProcurementItem, requirement and
 * material linkage). It does NOT execute a subcontract, issue a purchase
 * order, authorize payment, or notify the provider externally — the UI says
 * so before confirmation and repeats the backend's own enumeration after.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  awardWorkflowApi,
  classifyWorkflowError,
} from "../../services/intelligenceService";
import { hasCapability } from "../../lib/capabilities";
import { useAuth } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Link2,
  ShieldCheck,
  ShieldAlert,
  Loader2,
  Lock,
  ChevronRight,
} from "lucide-react";

const NOT_DONE = [
  "No subcontract executed",
  "No purchase order issued",
  "No payment authorized",
  "No external notification sent",
];

/** The authorized state, rendered from a fresh response or stored linkage. */
export const AuthorizedLinkageSummary = ({ linkage, result, projectId }) => {
  const navigate = useNavigate();
  const missing = result?.missingInformation || linkage?.missingInformation || [];
  return (
    <Card className="border-teal-500/40 bg-teal-500/5">
      <CardContent className="py-4 px-4 space-y-3">
        <p className="text-sm font-semibold text-teal-400 flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4" />
          AUTHORIZED TO PROCEED
          {result?.idempotent && (
            <Badge variant="outline" className="bg-muted/50 border-border font-normal">
              already authorized — no records changed
            </Badge>
          )}
        </p>
        {(result?.message || null) && (
          <p className="text-xs text-muted-foreground">{result.message}</p>
        )}
        {linkage?.selectedBidderName && (
          <p className="text-sm">
            {linkage.selectedBidderName} is now linked into procurement and execution truth.
          </p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-muted-foreground">
          <div>
            <p className="font-medium text-foreground">Bid package</p>
            <p>{linkage?.bidPackage?.status ? `status: ${linkage.bidPackage.status}` : "linked"}</p>
          </div>
          <div>
            <p className="font-medium text-foreground">Procurement item</p>
            {linkage?.procurementItemId ? (
              <button
                type="button"
                className="text-primary hover:underline inline-flex items-center gap-0.5"
                onClick={() => navigate(`/procurement${projectId ? `?project_id=${projectId}` : ""}`)}
              >
                Open
                <ChevronRight className="w-3 h-3" />
              </button>
            ) : (
              <p>none created</p>
            )}
          </div>
          <div>
            <p className="font-medium text-foreground">Requirements linked</p>
            <p>{(linkage?.linkedRequirementIds || []).length}</p>
          </div>
          <div>
            <p className="font-medium text-foreground">Materials linked</p>
            <p>{(linkage?.linkedMaterialStatusIds || []).length}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {NOT_DONE.map((line) => (
            <Badge key={line} variant="outline" className="bg-muted/50 border-border font-normal">
              {line}
            </Badge>
          ))}
        </div>
        {(result?.deferredActions || []).length > 0 && (
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-muted-foreground">Deferred actions</p>
            {result.deferredActions.map((d, i) => (
              <p key={i} className="text-xs text-muted-foreground">
                {typeof d === "string" ? d : d.description || d.target || JSON.stringify(d)}
              </p>
            ))}
          </div>
        )}
        {missing.length > 0 && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 space-y-0.5">
            <p className="text-xs font-semibold text-amber-400">
              Facts the linkage could not establish without guessing
            </p>
            {missing.map((m, i) => (
              <p key={i} className="text-xs text-muted-foreground">
                {m}
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ExecutionAuthorizationPanel = ({ packageId, proposal, user, onAuthorized }) => {
  // The session's capability answer, read from context rather than
  // threaded as a prop: `user` already arrives from the parent, and a
  // second prop carrying the SAME authority question invites the two to
  // be passed from different places. Null-safe so rendering this panel
  // outside the provider offers nothing rather than throwing.
  const { capabilities, capabilitiesEstablished } = useAuth() || {};
  /**
   * INDEPENDENT OF THE DECISION GUARD, ON PURPOSE.
   *
   * Approval judged the recommendation; authorization directs the linkage.
   * They are two separate human acts against two separate endpoints, so they
   * get two separate guards -- a shared one would let either act suppress the
   * other, which is a different bug wearing the same clothes.
   *
   *   authorizeInFlight -- per-request mutex, set synchronously before the
   *     await and released when this request settles either way. A stale 409
   *     or a transport failure must leave the control usable.
   *
   *   authorizedRef -- the semantic latch, set only once the server has
   *     recorded the authorization. Not released in `finally`: the parent's
   *     reload is what finally swaps this panel for the summary, and until it
   *     lands a second confirm would be a second consequential linkage.
   */
  const authorizeInFlight = useRef(false);
  const authorizedRef = useRef(false);
  useEffect(() => {
    authorizeInFlight.current = false;
    authorizedRef.current = false;
  }, [proposal?.proposalId]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const storedLinkage = proposal?.recommendation?.executionLinkage || null;
  const authorized = proposal?.state === "AUTHORIZED_TO_PROCEED" || Boolean(result);
  const stale = Boolean(proposal?.staleness?.stale);
  const allowed = hasCapability(capabilities, "award.authorize");

  if (proposal?.state !== "APPROVED" && !authorized) return null;

  if (authorized) {
    return (
      <AuthorizedLinkageSummary
        linkage={result?.linkage || storedLinkage}
        result={result}
        projectId={proposal?.projectId}
      />
    );
  }

  const authorize = async () => {
    // Synchronous, before any await. See the refs above.
    if (authorizeInFlight.current || authorizedRef.current) return;
    authorizeInFlight.current = true;
    setSubmitting(true);
    try {
      const response = await awardWorkflowApi.authorizeExecution(packageId, {
        proposalId: proposal.proposalId,
        note: note.trim() || undefined,
      });
      // Linked. Stays linked while the parent reload is still in flight.
      authorizedRef.current = true;
      setResult(response);
      setDialogOpen(false);
      toast.success("Authorized to proceed. No subcontract, PO, payment, or notification was issued.");
      onAuthorized?.();
    } catch (err) {
      const c = classifyWorkflowError(err);
      if (c.kind === "stale") {
        toast.error("The commercial facts moved since this proposal was approved — re-run the recommendation before authorizing.");
      } else {
        toast.error(c.message);
      }
      // Refresh only when the server said the facts moved; a transient
      // failure keeps the dialog (and the typed note) so the user can retry.
      if (c.kind === "stale" || c.kind === "conflict") onAuthorized?.();
    } finally {
      // Mutex releases; latch does not.
      authorizeInFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card className="border-teal-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Link2 className="w-4 h-4 text-teal-400" />
            Authorize Execution Handoff
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            The recommendation is approved. This separate step links the selected provider
            into OMNΛS procurement/execution truth — the package, the winning quote, one
            procurement item, and the requirements and materials behind them.
          </p>
          <p className="text-xs text-muted-foreground">
            It does <span className="font-medium text-foreground">not</span> execute a
            subcontract, issue a purchase order, authorize payment, or notify the provider
            externally.
          </p>
          {stale ? (
            <p className="text-xs text-red-400 flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5" />
              The proposal is stale — authorization is blocked until the recommendation is
              re-run against current facts.
            </p>
          ) : allowed ? (
            <Button size="sm" onClick={() => setDialogOpen(true)} disabled={submitting}>
              <ShieldCheck className="w-4 h-4 mr-1.5" />
              Authorize execution
            </Button>
          ) : capabilitiesEstablished ? (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5" />
              Execution authorization requires the award.authorize capability and its
              project-manager/admin role floor.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">Checking session authority…</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Authorize execution handoff</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This links {proposal.recommendedBidderName || "the selected provider"} into
              OMNΛS procurement/execution truth. It does not execute a subcontract, issue a
              purchase order, authorize payment, or notify anyone externally — those remain
              separate, deliberate steps.
            </p>
            <div>
              <Label className="text-xs">Note (optional, kept on the record)</Label>
              <Textarea className="mt-1" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={authorize} disabled={submitting}>
                {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                Confirm authorization
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExecutionAuthorizationPanel;
